import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Skeleton } from '../components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { toast } from 'sonner';
import {
  RefreshCw, Clock, CheckCircle, XCircle, PlusCircle,
  Zap, Flame, ChevronDown, ChevronRight, BarChart3,
  AlertTriangle, User, FileText, Loader2, MessageCircle,
  ThumbsUp, ThumbsDown, Search, Trash2, CalendarClock
} from 'lucide-react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
  DropdownMenuLabel, DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATI_RINNOVO = [
  { value: 'da_lavorare', label: 'Da Lavorare', color: 'bg-amber-100 text-amber-700 border-amber-200', icon: Clock },
  { value: 'interessato', label: 'Interessato', color: 'bg-emerald-100 text-emerald-700 border-emerald-200', icon: CheckCircle },
  { value: 'non_interessato', label: 'Non Interessato', color: 'bg-red-100 text-red-700 border-red-200', icon: XCircle },
  { value: 'pratica_creata', label: 'Pratica Creata', color: 'bg-sky-100 text-sky-700 border-sky-200', icon: FileText },
];

const Rinnovi = () => {
  const { hasRole } = useAuth();
  const navigate = useNavigate();
  const [rinnovi, setRinnovi] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingStats, setLoadingStats] = useState(false);
  const [filtroStato, setFiltroStato] = useState('da_lavorare');
  const [checking, setChecking] = useState(false);
  const [processing, setProcessing] = useState(null);
  const [showStats, setShowStats] = useState(false);
  const [expandedPod, setExpandedPod] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  const fetchRinnovi = useCallback(async () => {
    try {
      const params = filtroStato && filtroStato !== 'tutti' ? `?stato=${filtroStato}` : '';
      const res = await axios.get(`${API_URL}/api/rinnovi${params}`);
      setRinnovi(res.data);
    } catch (e) {
      toast.error('Errore nel caricamento rinnovi');
    } finally {
      setLoading(false);
    }
  }, [filtroStato]);

  const fetchStats = useCallback(async () => {
    setLoadingStats(true);
    try {
      const res = await axios.get(`${API_URL}/api/rinnovi/stats/pod-pdr`);
      setStats(res.data);
    } catch (e) {
      toast.error('Errore nel caricamento statistiche');
    } finally {
      setLoadingStats(false);
    }
  }, []);

  useEffect(() => { fetchRinnovi(); }, [fetchRinnovi]);

  const handleCheckNow = async () => {
    setChecking(true);
    try {
      const res = await axios.post(`${API_URL}/api/rinnovi/check-now`);
      toast.success(res.data.message);
      fetchRinnovi();
    } catch (e) {
      toast.error('Errore nel controllo rinnovi');
    } finally {
      setChecking(false);
    }
  };

  const handleEsita = async (id, stato) => {
    setProcessing(id);
    try {
      await axios.put(`${API_URL}/api/rinnovi/${id}/esita`, { stato });
      toast.success(stato === 'interessato' ? 'Cliente interessato!' : 'Segnato come non interessato');
      fetchRinnovi();
    } catch (e) {
      toast.error('Errore nell\'aggiornamento');
    } finally {
      setProcessing(null);
    }
  };

  const handleCreaPratica = async (id) => {
    setProcessing(id);
    try {
      const res = await axios.post(`${API_URL}/api/rinnovi/${id}/crea-pratica`);
      toast.success('Nuova pratica creata!');
      navigate(`/contracts/${res.data.contract_id}`);
    } catch (e) {
      toast.error('Errore nella creazione della pratica');
    } finally {
      setProcessing(null);
    }
  };

  const handleEliminaRinnovo = async (id) => {
    if (!window.confirm("Confermi di eliminare definitivamente questo rinnovo? Il cliente non verra' piu' ricontattato.")) return;
    setProcessing(id);
    try {
      await axios.delete(`${API_URL}/api/rinnovi/${id}`);
      toast.success('Rinnovo eliminato');
      fetchRinnovi();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore eliminazione rinnovo');
    } finally {
      setProcessing(null);
    }
  };

  const handlePosticipa = async (id, mesi) => {
    setProcessing(id);
    try {
      await axios.post(`${API_URL}/api/rinnovi/${id}/posticipa`, { mesi });
      toast.success(`Rinnovo posticipato di ${mesi} mesi`);
      fetchRinnovi();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore posticipa rinnovo');
    } finally {
      setProcessing(null);
    }
  };

  const formatDate = (d) => {
    if (!d) return '-';
    return new Date(d).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const getMesiTrascorsi = (dataContratto) => {
    if (!dataContratto) return 0;
    const diff = new Date() - new Date(dataContratto);
    return Math.floor(diff / (1000 * 60 * 60 * 24 * 30));
  };

  const getStatoInfo = (stato) => STATI_RINNOVO.find(s => s.value === stato) || STATI_RINNOVO[0];

  const getWhatsAppUrl = (r) => {
    const nome = r.cliente_nome?.split(' ')[0] || 'Cliente';
    const cognome = r.cliente_nome?.split(' ').slice(1).join(' ') || '';
    const mesi = getMesiTrascorsi(r.data_contratto);
    const tipo = r.tipologia === 'gas' ? 'gas' : r.tipologia === 'dual' ? 'luce e gas' : 'energia elettrica';
    const msg = `Gentile ${nome} ${cognome},\n\nLa contattiamo per informarLa che sono trascorsi ${mesi} mesi dalla sottoscrizione del Suo contratto di fornitura ${tipo}.\n\nNel frattempo il mercato energetico si e aggiornato e potrebbero essere disponibili offerte piu vantaggiose per Lei.\n\nSe fosse interessato/a, saremo lieti di effettuare una valutazione gratuita delle migliori tariffe attualmente disponibili.\n\nResta a disposizione per qualsiasi informazione.\n\nCordiali saluti`;
    const tel = (r.cliente_telefono || '').replace(/[^0-9+]/g, '');
    return `https://wa.me/${tel.replace('+', '')}?text=${encodeURIComponent(msg)}`;
  };

  if (loading) {
    return <div className="space-y-6"><Skeleton className="h-10 w-full max-w-md" /><Skeleton className="h-96 w-full" /></div>;
  }

  const daLavorare = rinnovi.filter(r => r.stato === 'da_lavorare').length;

  // Client-side search filter (name, POD, PDR, fornitore)
  const filteredRinnovi = rinnovi.filter((r) => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase().trim();
    return (
      (r.cliente_nome || '').toLowerCase().includes(q) ||
      (r.fornitore || '').toLowerCase().includes(q) ||
      (r.pod || '').toLowerCase().includes(q) ||
      (r.pdr || '').toLowerCase().includes(q) ||
      (r.offerta || '').toLowerCase().includes(q)
    );
  });

  return (
    <TooltipProvider>
    <div className="space-y-6" data-testid="rinnovi-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Rinnovi</h1>
          <p className="text-slate-500 mt-1">
            {daLavorare > 0 ? `${daLavorare} clienti da ricontattare` : 'Nessun rinnovo in attesa'}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline" size="sm"
            onClick={() => { setShowStats(!showStats); if (!stats) fetchStats(); }}
            data-testid="toggle-stats-btn"
          >
            <BarChart3 className="w-4 h-4 mr-1.5" />
            Statistiche POD/PDR
          </Button>
          {hasRole(['super_admin', 'admin']) && (
            <Button size="sm" onClick={handleCheckNow} disabled={checking} data-testid="check-rinnovi-btn">
              <RefreshCw className={`w-4 h-4 mr-1.5 ${checking ? 'animate-spin' : ''}`} />
              {checking ? 'Controllo...' : 'Controlla Ora'}
            </Button>
          )}
        </div>
      </div>

      {/* Stats Panel */}
      {showStats && (
        <Card data-testid="stats-panel">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-sky-600" />
              Statistiche POD/PDR - Anno {stats?.anno || new Date().getFullYear()}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loadingStats ? (
              <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
            ) : !stats || stats.pod_pdr_stats.length === 0 ? (
              <p className="text-center text-slate-500 py-6">Nessun dato disponibile per quest'anno</p>
            ) : (
              <div className="space-y-2">
                <p className="text-sm text-slate-500 mb-3">
                  Totale contratti nell'anno: <strong>{stats.totale_contratti_anno}</strong>
                </p>
                {stats.pod_pdr_stats.map((item) => (
                  <div key={`${item.tipo}-${item.codice}`} className="border rounded-lg overflow-hidden" data-testid={`stat-${item.codice}`}>
                    <div
                      className="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-slate-50 transition-colors"
                      onClick={() => setExpandedPod(expandedPod === item.codice ? null : item.codice)}
                    >
                      <div className="flex items-center gap-3">
                        {expandedPod === item.codice ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
                        <Badge variant="outline" className="text-xs">{item.tipo}</Badge>
                        <span className="font-mono text-sm font-medium">{item.codice}</span>
                        <span className="text-sm text-slate-500">({item.cliente})</span>
                      </div>
                      <Badge className={`${item.contratti > 1 ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600'}`}>
                        {item.contratti} {item.contratti === 1 ? 'contratto' : 'contratti'}
                      </Badge>
                    </div>
                    {expandedPod === item.codice && item.dettagli && (
                      <div className="border-t bg-slate-50 px-4 py-3">
                        <div className="space-y-2">
                          {item.dettagli.map((d, idx) => (
                            <div key={idx} className="flex items-center gap-4 text-sm">
                              <span className="w-5 h-5 rounded-full bg-slate-200 text-xs flex items-center justify-center font-bold text-slate-500">{idx + 1}</span>
                              <span className="font-medium">{d.fornitore || 'N/D'}</span>
                              <Badge variant="outline" className="text-xs">{d.tipologia?.toUpperCase()}</Badge>
                              <span className="text-slate-500">{d.stato}</span>
                              <span className="text-slate-400 ml-auto">{d.data}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Filter + Search */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <Select value={filtroStato} onValueChange={setFiltroStato}>
          <SelectTrigger className="w-full sm:w-48" data-testid="filter-stato-rinnovi">
            <SelectValue placeholder="Filtra per stato" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="tutti">Tutti</SelectItem>
            {STATI_RINNOVO.map(s => (
              <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
          <Input
            type="text"
            placeholder="Cerca per nome, POD o fornitore…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 h-10"
            data-testid="search-rinnovi-input"
          />
        </div>
        <span className="text-sm text-slate-500 sm:ml-auto">{filteredRinnovi.length} risultati</span>
      </div>

      {/* Rinnovi List */}
      <div className="space-y-3">
        {filteredRinnovi.length === 0 ? (
          <Card>
            <CardContent className="py-16 text-center">
              <CheckCircle className="w-12 h-12 mx-auto mb-4 text-emerald-300" />
              <p className="text-slate-500 text-lg">
                {searchQuery ? 'Nessun risultato per la ricerca' :
                  filtroStato === 'da_lavorare' ? 'Nessun rinnovo in attesa!' : 'Nessun rinnovo trovato'}
              </p>
              <p className="text-slate-400 text-sm mt-1">I contratti con piu di 4 mesi verranno segnalati automaticamente</p>
            </CardContent>
          </Card>
        ) : (
          filteredRinnovi.map((r) => {
            const statoInfo = getStatoInfo(r.stato);
            const mesi = getMesiTrascorsi(r.data_contratto);
            const StatoIcon = statoInfo.icon;
            const isProcessing = processing === r.id;

            return (
              <Card key={r.id} data-testid={`rinnovo-${r.id}`} className={`${r.stato === 'da_lavorare' ? 'border-amber-200 bg-amber-50/30' : ''}`}>
                <CardContent className="p-5">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-2">
                        <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border ${statoInfo.color}`}>
                          <StatoIcon className="w-3 h-3" />
                          {statoInfo.label}
                        </span>
                        {r.tipologia && (
                          <Badge variant="outline" className="text-xs">
                            {r.tipologia === 'gas' ? <Flame className="w-3 h-3 mr-1" /> : <Zap className="w-3 h-3 mr-1" />}
                            {r.tipologia.toUpperCase()}
                          </Badge>
                        )}
                        {mesi >= 6 && (
                          <Badge className="bg-red-100 text-red-700 text-xs">
                            <AlertTriangle className="w-3 h-3 mr-1" />{mesi} mesi
                          </Badge>
                        )}
                      </div>

                      <p className="text-lg font-semibold text-slate-900">{r.cliente_nome}</p>

                      <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-sm text-slate-500">
                        {r.fornitore && <span className="truncate">Fornitore: <strong className="text-slate-700">{r.fornitore}</strong></span>}
                        {r.offerta && <span className="truncate">Offerta: <strong className="text-slate-700">{r.offerta}</strong></span>}
                      </div>
                      <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-sm text-slate-500">
                        {r.pod && <span className="font-mono text-xs">POD: {r.pod}</span>}
                        {r.pdr && <span className="font-mono text-xs">PDR: {r.pdr}</span>}
                      </div>

                      <div className="flex items-center gap-4 mt-2 text-xs text-slate-400 flex-wrap">
                        <span>Contratto: {formatDate(r.data_contratto)}</span>
                        <span>{mesi} mesi fa</span>
                        {r.data_ricontatto_prevista && (
                          <span className="flex items-center gap-1 text-sky-600 font-medium">
                            <RefreshCw className="w-3 h-3" />
                            Ricontatto pianificato: {formatDate(r.data_ricontatto_prevista)}
                          </span>
                        )}
                        {r.mesi_rinnovo && !r.data_ricontatto_prevista && (
                          <span className="text-sky-600">Preset {r.mesi_rinnovo}m</span>
                        )}
                        {r.agente_nome && (
                          <span className="flex items-center gap-1"><User className="w-3 h-3" />{r.agente_nome}</span>
                        )}
                      </div>
                    </div>

                    {/* Actions */}
                    {r.stato === 'da_lavorare' && (
                      <div className="flex flex-wrap items-center gap-2 shrink-0">
                        {/* Primary actions */}
                        <Button
                          size="sm"
                          onClick={() => handleCreaPratica(r.id)}
                          disabled={isProcessing}
                          className="bg-slate-900 hover:bg-slate-800 text-white"
                          data-testid={`crea-pratica-rinnovo-${r.id}`}
                        >
                          <PlusCircle className="w-4 h-4 mr-1" /> Nuova Pratica
                        </Button>
                        {r.cliente_telefono && (
                          <a href={getWhatsAppUrl(r)} target="_blank" rel="noopener noreferrer">
                            <Button
                              size="sm"
                              className="bg-sky-600 hover:bg-sky-700 text-white"
                              data-testid={`whatsapp-rinnovo-${r.id}`}
                            >
                              <MessageCircle className="w-4 h-4 mr-1" /> Invia Messaggio
                            </Button>
                          </a>
                        )}
                        {/* Secondary icon actions */}
                        <div className="flex items-center gap-1 pl-1 border-l border-slate-200 ml-1">
                          <Tooltip delayDuration={100}>
                            <TooltipTrigger asChild>
                              <button
                                type="button"
                                onClick={() => handleEsita(r.id, 'interessato')}
                                disabled={isProcessing}
                                className="flex items-center justify-center w-9 h-9 rounded-md bg-emerald-50 hover:bg-emerald-100 text-emerald-600 transition-colors disabled:opacity-40"
                                data-testid={`esita-interessato-${r.id}`}
                                aria-label="Segna come interessato"
                              >
                                <ThumbsUp className="w-4 h-4" />
                              </button>
                            </TooltipTrigger>
                            <TooltipContent>Segna come interessato</TooltipContent>
                          </Tooltip>
                          <Tooltip delayDuration={100}>
                            <TooltipTrigger asChild>
                              <button
                                type="button"
                                onClick={() => handleEsita(r.id, 'non_interessato')}
                                disabled={isProcessing}
                                className="flex items-center justify-center w-9 h-9 rounded-md bg-red-50 hover:bg-red-100 text-red-600 transition-colors disabled:opacity-40"
                                data-testid={`esita-non-interessato-${r.id}`}
                                aria-label="Segna come non interessato"
                              >
                                <ThumbsDown className="w-4 h-4" />
                              </button>
                            </TooltipTrigger>
                            <TooltipContent>Segna come non interessato</TooltipContent>
                          </Tooltip>
                          <DropdownMenu>
                            <Tooltip delayDuration={100}>
                              <TooltipTrigger asChild>
                                <DropdownMenuTrigger asChild>
                                  <button
                                    type="button"
                                    disabled={isProcessing}
                                    className="flex items-center justify-center w-9 h-9 rounded-md bg-sky-50 hover:bg-sky-100 text-sky-600 transition-colors disabled:opacity-40"
                                    data-testid={`posticipa-btn-${r.id}`}
                                    aria-label="Posticipa il ricontatto"
                                  >
                                    <CalendarClock className="w-4 h-4" />
                                  </button>
                                </DropdownMenuTrigger>
                              </TooltipTrigger>
                              <TooltipContent>Posticipa ricontatto</TooltipContent>
                            </Tooltip>
                            <DropdownMenuContent align="end">
                              <DropdownMenuLabel>Riprova tra</DropdownMenuLabel>
                              <DropdownMenuSeparator />
                              {[2, 4, 6, 8, 10, 12].map((m) => (
                                <DropdownMenuItem
                                  key={m}
                                  onClick={() => handlePosticipa(r.id, m)}
                                  data-testid={`posticipa-${m}-mesi-${r.id}`}
                                >
                                  {m} mesi
                                </DropdownMenuItem>
                              ))}
                            </DropdownMenuContent>
                          </DropdownMenu>
                          <Tooltip delayDuration={100}>
                            <TooltipTrigger asChild>
                              <button
                                type="button"
                                onClick={() => handleEliminaRinnovo(r.id)}
                                disabled={isProcessing}
                                className="flex items-center justify-center w-9 h-9 rounded-md bg-slate-100 hover:bg-red-100 text-slate-500 hover:text-red-600 transition-colors disabled:opacity-40"
                                data-testid={`elimina-rinnovo-${r.id}`}
                                aria-label="Elimina rinnovo"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </TooltipTrigger>
                            <TooltipContent>Elimina rinnovo</TooltipContent>
                          </Tooltip>
                        </div>
                      </div>
                    )}
                    {r.stato === 'interessato' && (
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          onClick={() => handleCreaPratica(r.id)}
                          disabled={isProcessing}
                          className="bg-slate-900 hover:bg-slate-800 text-white"
                          data-testid={`crea-pratica-rinnovo-${r.id}`}
                        >
                          <PlusCircle className="w-4 h-4 mr-1" /> Crea Pratica
                        </Button>
                        {r.cliente_telefono && (
                          <a href={getWhatsAppUrl(r)} target="_blank" rel="noopener noreferrer">
                            <Button size="sm" className="bg-sky-600 hover:bg-sky-700 text-white" data-testid={`whatsapp-rinnovo-${r.id}`}>
                              <MessageCircle className="w-4 h-4 mr-1" /> Messaggio
                            </Button>
                          </a>
                        )}
                      </div>
                    )}
                    {r.stato === 'pratica_creata' && r.nuovo_contract_id && (
                      <Button
                        size="sm" variant="outline"
                        onClick={() => navigate(`/contracts/${r.nuovo_contract_id}`)}
                        data-testid={`vai-pratica-${r.id}`}
                      >
                        <FileText className="w-4 h-4 mr-1" /> Vai alla Pratica
                      </Button>
                    )}
                    {r.stato === 'non_interessato' && r.lavorato_at && (
                      <span className="text-xs text-slate-400">Esitato il {formatDate(r.lavorato_at)}</span>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>
    </div>
    </TooltipProvider>
  );
};

export default Rinnovi;
